import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, FileSpreadsheet, File as FileIcon, ClipboardCheck, ArrowRight } from "lucide-react";
import { PortalPageShell } from "@/pages/portal/PortalShared";
import { usePortal } from "@/pages/portal/PortalContext";

const iconFor = (label = "") => {
  const l = label.toLowerCase();
  if (l.endsWith(".xlsx") || l.endsWith(".xls") || l.endsWith(".csv") || l.includes("spreadsheet")) return FileSpreadsheet;
  if (l.endsWith(".pdf")) return FileText;
  return FileText;
};

const prettyLabel = (d) => {
  // Fall back to nicer version rather than raw filename
  if (d.label && !/\.(pdf|xlsx|xls|csv|docx?)$/i.test(d.label)) return d.label;
  const base = (d.label || "").replace(/\.(pdf|xlsx|xls|csv|docx?)$/i, "");
  if (d.kind === "quote") return `Quote — ${base || d.status}`;
  if (d.kind === "contract") return `Contract — ${base || d.status}`;
  return base || d.kind;
};

const versionChip = (d) => {
  const m = (d.label || "").match(/v(\d+)/i);
  return m ? `v${m[1]}` : null;
};

const DocumentRow = ({ doc, i }) => {
  const Icon = iconFor(doc.label);
  const v = versionChip(doc);
  return (
    <li
      className="py-4 flex items-center gap-4"
      data-testid={`portal-doc-row-${i}`}
    >
      <div
        className="w-10 h-10 rounded-md grid place-items-center shrink-0"
        style={{ background: "var(--bg-inset)", color: "#1B4F8A" }}
      >
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold truncate" style={{ color: "var(--fg-primary)" }}>
          {prettyLabel(doc)}
        </div>
        <div className="text-[12px] mt-0.5 flex items-center gap-2 flex-wrap" style={{ color: "var(--fg-muted)" }}>
          <span className="uppercase tracking-[0.10em]">{doc.kind}</span>
          {v && (
            <span
              className="uppercase text-[10px] tracking-[0.14em] px-1.5 py-0.5 rounded-sm font-semibold"
              style={{ background: "var(--bg-inset)", color: "var(--fg-primary)" }}
            >{v}</span>
          )}
          <span>·</span>
          <span>{doc.status}</span>
        </div>
      </div>
      <a
        href={`${process.env.REACT_APP_BACKEND_URL}${doc.pdf_url}`}
        target="_blank" rel="noreferrer"
        className="inline-flex items-center h-9 px-3 rounded-md text-[12.5px] font-semibold"
        style={{ color: "#fff", background: "#1B4F8A" }}
        data-testid={`portal-doc-view-${i}`}
      >
        View
      </a>
    </li>
  );
};

/* ── Pickup Sheet Cross-Link Discovery (server-derived) ── */
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtStartDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

const statusLabelFor = (la) => {
  if (!la) return "Pending Review";
  if (la.status === "approved") return "Client Approved";
  if (la.status === "changes_requested") return "Changes Requested";
  return "Pending Review";
};

const PickupSheetCard = ({ sheet, navigate, idx }) => (
  <button
    onClick={() =>
      navigate(
        `/portal/pickup-review?tour=${sheet.tour_id}&da=${sheet.da_id}`
      )
    }
    className="w-full text-left rounded-md p-4 flex items-center gap-4 transition-colors group"
    style={{
      background: "var(--bg-surface)",
      border: "1px solid var(--border-default)",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = "var(--border-strong)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = "var(--border-default)";
    }}
    data-testid={idx === 0 ? "portal-docs-pickup-review-link" : `portal-docs-pickup-review-link-${idx}`}
  >
    <div
      className="w-10 h-10 rounded-md grid place-items-center shrink-0"
      style={{ background: "rgba(183,227,29,0.10)", color: "#B7E31D" }}
    >
      <ClipboardCheck size={18} />
    </div>
    <div className="flex-1 min-w-0">
      <div
        className="text-[14px] font-semibold truncate"
        style={{ color: "var(--fg-primary)" }}
      >
        {sheet.tour_name} — {sheet.driver_display_name}
      </div>
      <div
        className="text-[12px] mt-0.5 flex items-center gap-2 flex-wrap"
        style={{ color: "var(--fg-muted)" }}
      >
        <span>{statusLabelFor(sheet.last_approval)}</span>
        {sheet.start_date && (
          <>
            <span>·</span>
            <span>{fmtStartDate(sheet.start_date)}</span>
          </>
        )}
      </div>
    </div>
    <ArrowRight
      size={16}
      className="shrink-0 opacity-40 group-hover:opacity-80 transition-opacity"
      style={{ color: "var(--fg-primary)" }}
    />
  </button>
);

const PickupReviewCrossLink = () => {
  const [sheets, setSheets] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();
  const { loading: ctxLoading, error: ctxError } = usePortal();

  useEffect(() => {
    // Gate: do not fire until PortalContext has finished bootstrapping
    // and no auth error occurred. Prevents a race where this fetch runs
    // before the token is fully validated by context.
    if (ctxLoading || ctxError) return;

    let cancelled = false;
    (async () => {
      try {
        const t = localStorage.getItem("aa_portal_token");
        if (!t) { setLoaded(true); return; }
        const res = await fetch(
          `${BACKEND}/api/portal/pickup-sheets/available`,
          { headers: { Authorization: `Bearer ${t}` } }
        );
        if (!res.ok) { setLoaded(true); return; }
        const json = await res.json();
        if (!cancelled) setSheets(json.items || []);
      } catch {
        /* silent — discovery is best-effort */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [ctxLoading, ctxError]);

  if (!loaded || sheets.length === 0) return null;

  return (
    <section className="py-4" data-testid="portal-docs-pickup-crosslink">
      <div
        className="uppercase text-[10.5px] tracking-[0.14em] font-semibold mb-1"
        style={{ color: "var(--fg-muted)" }}
      >
        Pickup Sheets
      </div>
      <div className="space-y-2">
        {sheets.map((s, i) => (
          <PickupSheetCard
            key={`${s.tour_id}-${s.da_id}`}
            sheet={s}
            navigate={navigate}
            idx={i}
          />
        ))}
      </div>
    </section>
  );
};

export default function PortalDocuments() {
  const { docs, activeTour } = usePortal();

  if (!activeTour) {
    return (
      <PortalPageShell
        eyebrow="Documents"
        title="Documents"
        subtitle="No active tour on file yet."
        testid="page-portal-documents"
      />
    );
  }

  const groups = {
    quote:    { label: "Quotes",     items: [] },
    contract: { label: "Contracts",  items: [] },
    tour:     { label: "Tour Documents", items: [] },
  };
  (docs || []).forEach((d, i) => {
    const k = groups[d.kind] ? d.kind : "tour";
    groups[k].items.push({ ...d, _idx: i });
  });

  const empty = (docs || []).length === 0;

  return (
    <div className="max-w-[880px] mx-auto" data-testid="page-portal-documents">
      <PortalPageShell
        eyebrow="Documents"
        title="Documents"
        subtitle="Quotes, contracts, and tour documents from All Access."
      >
        {empty && (
          <div
            className="rounded-md p-6 text-[13.5px]"
            style={{
              background: "var(--bg-surface)",
              border: "1px solid var(--border-default)",
              color: "var(--fg-secondary)",
            }}
            data-testid="portal-docs-empty"
          >
            No documents available yet.
          </div>
        )}
        <PickupReviewCrossLink />

        {!empty && Object.entries(groups).map(([k, g]) => {
          if (g.items.length === 0) return null;
          return (
            <section key={k} className="py-4 first:pt-2" data-testid={`portal-docs-section-${k}`}>
              <div className="uppercase text-[10.5px] tracking-[0.14em] font-semibold mb-1"
                   style={{ color: "var(--fg-muted)" }}>
                {g.label}
              </div>
              <ul className="divide-y" style={{ borderColor: "var(--border-subtle)" }}>
                {g.items.map((d) => (
                  <DocumentRow key={d._idx} doc={d} i={d._idx} />
                ))}
              </ul>
            </section>
          );
        })}
      </PortalPageShell>
    </div>
  );
}
